// Downstream impact traversal (ARCHITECTURE.md §8.2, §8.3).
//
// "What breaks if this changes?" — follow reverse dependency edges from the
// changed files. The actual BFS lives in the shared `traverse` core; this
// module just fixes the direction to "downstream".

import type { EdgeClassification } from "../types.js";
import type { DependencyGraph } from "../dependency/types.js";
import type { EvidenceType } from "../evidence/types.js";
import type { AffectedEntry } from "./types.js";
import { traverse } from "./traverse.js";

export interface TraverseDownstreamOptions {
  graph: DependencyGraph;
  /** The changed files to start from. */
  changed: string[];
  /** Maximum BFS depth to explore; default: unlimited. */
  maxDepth?: number;
  /** Edge classifications to traverse; default: deterministic + candidate. */
  edgeTypes?: EdgeClassification[];
  /** Evidence types to exclude when scoring entry relevance. */
  evidenceExclude?: EvidenceType[];
  /** Cap the number of returned entries. */
  limit?: number;
  /** @deprecated Issue #21 — use `edgeTypes`. Honoured for backward compat. */
  minConfidence?: number;
}

/**
 * Compute the files affected downstream of `changed` by following reverse
 * dependency edges. Returns AffectedEntry[] sorted by depth ascending.
 */
export function traverseDownstream(
  options: TraverseDownstreamOptions,
): AffectedEntry[] {
  const { graph, changed, maxDepth, edgeTypes, evidenceExclude, limit, minConfidence } =
    options;
  return traverse({
    graph,
    seeds: changed,
    direction: "downstream",
    maxDepth,
    ...(edgeTypes ? { edgeTypes } : {}),
    ...(evidenceExclude ? { evidenceExclude } : {}),
    ...(limit !== undefined ? { limit } : {}),
    ...(minConfidence !== undefined ? { minConfidence } : {}),
  });
}
